import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDefaultProductVariant, getProductImageNodes, getProductVariants } from '../utils/productPresentation';

const CartContext = createContext(null);
const CART_KEY = 'vettail_cart';

function sanitizeQuantity(quantity) {
  const parsed = Number.parseInt(quantity, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function resolveVariant(product, variantOrId) {
  if (!variantOrId) return getDefaultProductVariant(product);
  if (typeof variantOrId === 'object') return variantOrId;
  return getProductVariants(product).find((variant) => variant.id === variantOrId) || { id: variantOrId };
}

function normalizeCartItem(product, variant, variantId, quantity) {
  const productImages = getProductImageNodes(product);
  const variantImage = variant?.image?.url ? variant.image : null;
  const images = variantImage
    ? [variantImage, ...productImages.filter((img) => img?.url && img.url !== variantImage.url)]
    : productImages;

  return {
    id: product.id,
    handle: product.handle,
    title: product.title,
    images,
    price: variant?.price || product.price || product.priceRange?.minVariantPrice || null,
    variantId,
    variantTitle: variant?.title || product.variantTitle || null,
    quantity,
  };
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => ({
          ...item,
          quantity: sanitizeQuantity(item.quantity),
        }))
        .filter((item) => item.variantId && item.quantity > 0);
    } catch {
      return [];
    }
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  function addToCart(product, variantOrId, quantity = 1) {
    const variant = resolveVariant(product, variantOrId);
    const variantId = variant?.id || (typeof variantOrId === 'string' ? variantOrId : null);
    const normalizedQuantity = sanitizeQuantity(quantity);
    if (!variantId || normalizedQuantity < 1) return;

    const nextItem = normalizeCartItem(product, variant, variantId, normalizedQuantity);

    setCartItems((prev) => {
      const existing = prev.find((item) => item.variantId === variantId);
      if (existing) {
        return prev.map((item) =>
          item.variantId === variantId
            ? { ...item, ...nextItem, quantity: item.quantity + normalizedQuantity }
            : item
        );
      }
      return [...prev, nextItem];
    });
  }

  function updateCartItemQuantity(variantId, quantity) {
    const normalizedQuantity = sanitizeQuantity(quantity);

    setCartItems((prev) => {
      if (normalizedQuantity < 1) {
        return prev.filter((item) => item.variantId !== variantId);
      }

      return prev.map((item) =>
        item.variantId === variantId
          ? { ...item, quantity: normalizedQuantity }
          : item
      );
    });
  }

  function removeFromCart(variantId) {
    setCartItems((prev) => prev.filter((item) => item.variantId !== variantId));
  }

  const total = cartItems.reduce((sum, item) => {
    return sum + parseFloat(item.price?.amount ?? 0) * item.quantity;
  }, 0);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, updateCartItemQuantity, removeFromCart, total }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
